/**
 * @file Contexte de layout pleine hauteur piloté par la page affichée.
 * @module layouts.app.full-height-context
 *
 * Certaines pages (réservations restaurant, éditeur de plan de salle) gèrent
 * leur propre scroll interne et doivent occuper toute la hauteur de l'écran
 * plutôt que de laisser le body scroller. Plutôt que de recenser leurs URLs
 * dans le layout — couche partagée qui n'a pas à connaître les routes d'une app
 * consommatrice — chaque page déclare elle-même ce besoin via
 * `useFullHeightLayout(breakpoint)`.
 *
 * Le breakpoint vit ainsi à côté du layout responsive de la page (même endroit
 * que ses `sx` `{xs, md}`), ce qui en fait la source unique : impossible de
 * désynchroniser le seuil de bascule deux-colonnes de la page et le seuil
 * d'activation du pleine-hauteur. `packages/common` reste agnostique de l'app.
 *
 * Deux contextes distincts sont exposés : l'API d'enregistrement
 * (register/unregister), d'identité stable pour la durée de vie du provider, et
 * la valeur courante du breakpoint. L'effet d'enregistrement ne dépend que de
 * l'API stable : il ne se ré-exécute donc qu'au montage/démontage de la page,
 * jamais à chaque changement de breakpoint.
 */
import PropTypes from 'prop-types';
import {createContext, useContext, useLayoutEffect, useMemo, useState} from 'react';

const FullHeightApiContext = createContext(null);
const FullHeightValueContext = createContext(null);

/**
 * Fournit l'état « quelle page réclame le mode pleine hauteur, et à partir de
 * quel breakpoint ». Doit envelopper à la fois le layout (qui lit le breakpoint)
 * et les pages rendues dans son Outlet (qui le déclarent).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @return {JSX.Element}
 */
export function FullHeightLayoutProvider({children}) {
  const [breakpoint, setBreakpoint] = useState(null);

  // API d'identité stable ([] en deps) : le hook d'enregistrement peut en
  // dépendre sans se ré-exécuter à chaque changement de breakpoint.
  const api = useMemo(() => ({
    register: (bp) => setBreakpoint(bp),
    // Ne libère que si le breakpoint courant est bien le nôtre : lors d'une
    // navigation entre deux pages pleine hauteur, le démontage de l'ancienne
    // ne doit pas effacer la déclaration déjà posée par la nouvelle.
    unregister: (bp) => setBreakpoint((current) => (current === bp ? null : current)),
  }), []);

  return (
    <FullHeightApiContext.Provider value={api}>
      <FullHeightValueContext.Provider value={breakpoint}>
        {children}
      </FullHeightValueContext.Provider>
    </FullHeightApiContext.Provider>
  );
}

FullHeightLayoutProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Lu par le layout : breakpoint (`'md'`, `'lg'`, …) à partir duquel la page
 * courante veut le mode pleine hauteur, ou `null` si aucune page ne le réclame.
 *
 * @return {string|null}
 */
export function useFullHeightBreakpoint() {
  return useContext(FullHeightValueContext);
}

/**
 * Appelé par une page pour réclamer le mode pleine hauteur du layout à partir du
 * breakpoint fourni (en dessous, scroll naturel du body). L'enregistrement est
 * posé en `useLayoutEffect` pour être appliqué avant le premier paint (pas de
 * flash), et retiré au démontage de la page.
 *
 * @param {string} breakpoint - Clé de breakpoint MUI (ex. `'md'`, `'lg'`)
 * @return {void}
 */
export function useFullHeightLayout(breakpoint = 'lg') {
  const api = useContext(FullHeightApiContext);

  useLayoutEffect(() => {
    if (!api) return undefined;
    api.register(breakpoint);
    return () => api.unregister(breakpoint);
  }, [api, breakpoint]);
}
